/*
 * Copyright (c) 2025 Sila Artificial Intelligence Research LLC
 * A DA Holding Ltd Company
 * All rights reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 * This software and its documentation are proprietary to Sila Artificial
 * Intelligence Research LLC. Unauthorized copying, distribution, or use
 * of this software is strictly prohibited.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL
 * THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING
 * FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER
 * DEALINGS IN THE SOFTWARE.
 */

import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useStreamingChat } from '../hooks/useStreamingChat';
import { EnhancedMessage } from '../components/EnhancedMessage';
import AutoExpandingTextArea from '../components/AutoExpandingTextArea';
import chatService from '../services/chatService';
import { useTheme } from '../contexts/ThemeContext';
import 'katex/dist/katex.min.css';
import 'highlight.js/styles/github-dark.css';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  metrics?: any;
  competitors?: any[];
  demographics?: any[];
  followUpQuestions?: string[];
  ragSources?: Array<{ title: string; content?: string; page?: number; relevance: number }>;
  showCharts?: boolean;
}


import { ChatSession } from '../services/chatService';

const LandingChatPage = () => {
  const { conversationId } = useParams<{ conversationId: string }>();
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [chatId, setChatId] = useState<string>('');
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [recentChats, setRecentChats] = useState<ChatSession[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoadingChats, setIsLoadingChats] = useState(false);
  const [deletingChatId, setDeletingChatId] = useState<string | null>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const { isStreaming, streamingContent, responseData, sendStreamingMessage } = useStreamingChat({
    onError: (error) => {
      console.error('Chat error:', error);
    }
  });

  // Rotating placeholder texts
  const placeholders = [
    "Ask me about brand health metrics...",
    "Explore traditional vs digital insights...",
    "Discover brand performance trends..."
  ];

  // Initialize chat ID or load existing conversation
  useEffect(() => {
    const loadConversation = async () => {
      if (conversationId) {
        // Load existing conversation
        try {
          const session = await chatService.getChatSession(conversationId);
          setChatId(conversationId);

          // Convert messages to the expected format
          const loadedMessages: Message[] = session.messages.map((msg, index) => ({
            id: `msg-${index}`,
            role: msg.role,
            content: msg.content,
            timestamp: new Date(msg.timestamp || new Date())
          }));

          setMessages(loadedMessages);
        } catch (error) {
          console.error('Failed to load conversation:', error);
          // If failed to load, start a new conversation
          const newChatId = chatService.generateChatId();
          setChatId(newChatId);
        }
      } else {
        // New conversation
        const newChatId = chatService.generateChatId();
        setChatId(newChatId);
      }
    };

    loadConversation();
  }, [conversationId]);

  // Load recent chats for sidebar
  const loadRecentChats = async () => {
    try {
      setIsLoadingChats(true);
      const chats = await chatService.getRecentChats(undefined, 50);
      setRecentChats(chats);
    } catch (error) {
      console.error('Failed to load recent chats:', error);
      // Fallback to demo data
      setRecentChats([
        { id: 'demo-1', title: 'Brand health analysis for Q4...', last_message: 'What are the key metrics?', message_count: 5, created_at: new Date().toISOString(), updated_at: new Date().toISOString() },
        { id: 'demo-2', title: 'Consumer insights and trends...', last_message: 'Show me demographics', message_count: 3, created_at: new Date(Date.now() - 86400000).toISOString(), updated_at: new Date(Date.now() - 86400000).toISOString() },
        { id: 'demo-3', title: 'Competitive benchmark review...', last_message: 'Compare with competitors', message_count: 4, created_at: new Date(Date.now() - 172800000).toISOString(), updated_at: new Date(Date.now() - 172800000).toISOString() }
      ]);
    } finally {
      setIsLoadingChats(false);
    }
  };

  useEffect(() => {
    loadRecentChats();
  }, []);

  // Reload chats when sidebar opens or messages change
  useEffect(() => {
    if (sidebarOpen) {
      loadRecentChats();
    }
  }, [sidebarOpen, messages.length]);

  // Delete chat function
  const handleDeleteChat = async (chatIdToDelete: string, e: React.MouseEvent) => {
    e.stopPropagation();

    if (!window.confirm('Delete this conversation?')) {
      return;
    }

    try {
      setDeletingChatId(chatIdToDelete);
      await chatService.deleteChatSession(chatIdToDelete);
      setRecentChats(prev => prev.filter(c => c.id !== chatIdToDelete));

      // If we deleted the current conversation, navigate to new chat
      if (conversationId === chatIdToDelete) {
        clearChat();
      }
    } catch (error) {
      console.error('Failed to delete chat:', error);
    } finally {
      setDeletingChatId(null);
    }
  };

  // Save chat to backend when messages update
  useEffect(() => {
    if (messages.length > 1 && chatId) { // At least one exchange
      // Save to backend
      const chatMessages = messages.map(m => ({
        role: m.role,
        content: m.content,
        timestamp: m.timestamp?.toISOString() || new Date().toISOString()
      }));

      // Save chat session to backend
      chatService.saveChatSession(
        chatId,
        'context-from-message', // Let backend extract brand from context
        chatMessages
      );
    }
  }, [messages, chatId]);

  // Focus input on mount and rotate placeholders
  useEffect(() => {
    inputRef.current?.focus();

    const interval = setInterval(() => {
      setPlaceholderIndex((prev) => (prev + 1) % placeholders.length);
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamingContent]);

  const handleSendMessage = async (input: string) => {
    if (!input.trim() || isStreaming) return;

    const userMessage: Message = {
      id: `msg-${Date.now()}-user`,
      role: 'user',
      content: input.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsTransitioning(true);

    // Start getting response (no placeholder needed since we get full response)
    await sendStreamingMessage(
      input.trim(),
      'context-from-message',
      messages.map(m => ({
        role: m.role,
        content: m.content,
        timestamp: m.timestamp
      }))
    );
  };

  // Add complete response when received
  useEffect(() => {
    if (streamingContent && !isStreaming && responseData) {
      // Add the complete assistant message with enhanced data
      const assistantMessage: Message = {
        id: `msg-${Date.now()}-assistant`,
        role: 'assistant',
        content: streamingContent,
        timestamp: new Date(),
        metrics: responseData.metrics,
        competitors: responseData.competitors,
        demographics: responseData.demographics,
        followUpQuestions: responseData.follow_up_questions,
        ragSources: responseData.rag_sources,
        showCharts: responseData.show_charts !== false
      };
      setMessages(prev => [...prev, assistantMessage]);
    }
  }, [streamingContent, isStreaming, responseData]);

  const handleSuggestionClick = (query: string) => {
    setInputValue(query);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleFollowUpClick = (question: string) => {
    handleSendMessage(question);
  };

  const clearChat = () => {
    setMessages([]);
    setInputValue('');
    setIsTransitioning(false);
    const newChatId = chatService.generateChatId();
    setChatId(newChatId);
    // Navigate to /chat for a fresh start
    navigate('/chat');
  };

  const hasMessages = messages.length > 0;

  const filteredChats = recentChats.filter(chat =>
    chat.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    chat.last_message.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleChatSelect = (selectedChatId: string) => {
    setSidebarOpen(false);
    navigate(`/chat/${selectedChatId}`);
  };

  // Format relative time
  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  // Sidebar Panel Component
  const ChatSidebar = () => (
    <>
      {/* Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/30 z-40 transition-opacity"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`fixed top-0 right-0 h-full w-80 bg-card border-l border-border shadow-2xl z-50 transform transition-transform duration-300 ease-in-out ${
        sidebarOpen ? 'translate-x-0' : 'translate-x-full'
      }`}>
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="p-4 border-b border-border flex items-center justify-between">
            <h2 className="text-lg font-semibold text-foreground">Chats</h2>
            <div className="flex items-center gap-2">
              <button
                onClick={loadRecentChats}
                disabled={isLoadingChats}
                className="p-2 hover:bg-background-secondary rounded-lg transition-colors"
                title="Refresh"
              >
                <svg className={`w-4 h-4 text-text-muted ${isLoadingChats ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
              <button
                onClick={() => setSidebarOpen(false)}
                className="p-2 hover:bg-background-secondary rounded-lg transition-colors"
              >
                <svg className="w-5 h-5 text-text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* New Chat Button */}
          <div className="p-4">
            <button
              onClick={() => {
                clearChat();
                setSidebarOpen(false);
              }}
              className="w-full flex items-center gap-3 px-4 py-3 bg-primary/10 hover:bg-primary/20 text-primary rounded-xl transition-colors"
            >
              <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                </svg>
              </div>
              <span className="font-medium">New chat</span>
            </button>
          </div>

          {/* Search */}
          <div className="px-4 pb-4">
            <div className="relative">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Search your chats..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-8 py-2.5 bg-background-secondary border border-border rounded-xl text-sm text-foreground placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-primary/50"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-foreground"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          </div>

          {/* Recent Chats */}
          <div className="flex-1 overflow-y-auto px-4">
            <p className="text-xs text-text-muted font-medium uppercase tracking-wider mb-3">
              Recents {recentChats.length > 0 && `(${recentChats.length})`}
            </p>

            {isLoadingChats ? (
              <div className="flex justify-center py-8">
                <svg className="w-6 h-6 text-primary animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
            ) : (
              <div className="space-y-1">
                {filteredChats.length > 0 ? (
                  filteredChats.map((chat) => (
                    <div
                      key={chat.id}
                      onClick={() => handleChatSelect(chat.id)}
                      className={`relative group px-3 py-2.5 rounded-lg transition-colors cursor-pointer hover:bg-background-secondary ${
                        conversationId === chat.id ? 'bg-background-secondary border-l-2 border-primary' : ''
                      }`}
                    >
                      <div className="pr-8">
                        <p className="text-sm text-foreground truncate group-hover:text-primary transition-colors">
                          {chat.title || 'New conversation'}
                        </p>
                        <p className="text-xs text-text-muted truncate mt-0.5">
                          {chat.last_message}
                        </p>
                        <p className="text-xs text-text-muted mt-1">
                          {formatRelativeTime(chat.updated_at)}
                        </p>
                      </div>

                      {/* Delete button */}
                      <button
                        onClick={(e) => handleDeleteChat(chat.id, e)}
                        disabled={deletingChatId === chat.id}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 opacity-0 group-hover:opacity-100 hover:bg-red-500/20 rounded-lg transition-all"
                        title="Delete conversation"
                      >
                        {deletingChatId === chat.id ? (
                          <svg className="w-4 h-4 text-red-500 animate-spin" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                        ) : (
                          <svg className="w-4 h-4 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        )}
                      </button>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <svg className="w-12 h-12 text-text-muted mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                    <p className="text-sm text-text-muted">
                      {searchQuery ? 'No chats match your search' : 'No conversations yet'}
                    </p>
                    {!searchQuery && (
                      <button
                        onClick={() => {
                          clearChat();
                          setSidebarOpen(false);
                        }}
                        className="mt-3 text-sm text-primary hover:underline"
                      >
                        Start your first chat
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );

  // Sidebar Toggle Button Component
  const SidebarToggleButton = ({ className = '' }: { className?: string }) => (
    <button
      onClick={() => setSidebarOpen(true)}
      className={`p-2 hover:bg-background-secondary border border-border rounded-lg transition-colors ${className}`}
      title="Open chat history"
    >
      <svg className="w-5 h-5 text-text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25H12" />
        <rect x="15" y="3" width="6" height="18" rx="1" stroke="currentColor" strokeWidth={1.5} fill="none" />
      </svg>
    </button>
  );

  // Landing view - centered input (no messages)
  if (!hasMessages) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background transition-all duration-500 relative">
        {/* Sidebar Toggle Button - Fixed on right */}
        <div className="fixed top-4 right-4 z-30">
          <SidebarToggleButton />
        </div>

        {/* Chat Sidebar */}
        <ChatSidebar />

        {/* Subtle background pattern */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute inset-0 bg-grid-pattern opacity-30"></div>
        </div>

        {/* Main content - all centered */}
        <div className="relative z-10 w-full max-w-4xl px-4">
          {/* Logo and title */}
          <div className={`text-center mb-12 transition-all duration-500 ${isTransitioning ? 'transform -translate-y-10 opacity-0' : ''}`}>
            <div className="flex flex-col items-center">
              {/* Sila Logo with beta badge */}
              <div className="w-40 h-14 relative flex items-center justify-center mb-0">
                <img
                  src="/sila-wordmark.png"
                  alt="Sila Logo"
                  className="w-full h-full object-contain"
                />
                <span className="absolute -top-1 -right-1 bg-gradient-to-r from-yellow-400 to-amber-500 text-black text-[10px] font-bold px-1.5 py-0.5 rounded-full uppercase shadow-sm">
                  beta
                </span>
              </div>

              {/* Title */}
              <div className="flex items-center justify-center -mt-1">
                <h1 className="text-base flex items-center">
                  <span className="font-light tracking-tight text-text-secondary">Brand</span>
                  <span className="font-semibold text-gradient ml-0.5">Health</span>
                  <span className="font-light tracking-tight text-text-secondary ml-0.5">360</span>
                  <i className="fas fa-heartbeat ml-1.5 text-sm text-primary" />
                </h1>
              </div>

              {/* Tagline */}
              <p className="text-sm text-text-muted mt-2">
                AI-powered brand health insights and analysis
              </p>
            </div>
          </div>

          {/* Centered chat input */}
          <div className={`relative transition-all duration-500 ${isTransitioning ? 'transform -translate-y-10 scale-95 opacity-0' : ''}`}>
            <div className="bg-card border border-border rounded-3xl p-2 shadow-lg hover:shadow-xl transition-all duration-300">
              <div className="flex items-center">
                <AutoExpandingTextArea
                  ref={inputRef}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage(inputValue);
                    }
                  }}
                  placeholder={placeholders[placeholderIndex]}
                  disabled={isStreaming}
                  className="flex-1 bg-transparent text-foreground placeholder-text-muted px-6 py-4
                    focus:outline-none text-lg transition-all duration-200 resize-none border-0"
                  rows={1}
                />

                <button
                  onClick={() => handleSendMessage(inputValue)}
                  disabled={!inputValue.trim() || isStreaming}
                  className={`mr-2 p-3 rounded-xl transition-all duration-200
                    ${!inputValue.trim() || isStreaming
                      ? 'text-text-muted cursor-not-allowed opacity-50'
                      : 'bg-primary hover:bg-primary/90 text-white shadow-md hover:shadow-lg transform hover:scale-105 active:scale-95'
                    }`}
                >
                  <i className={`fas ${isStreaming ? 'fa-spinner fa-spin' : 'fa-paper-plane'} text-lg`}></i>
                </button>
              </div>
            </div>
          </div>

          {/* Popular Questions */}
          <div className={`mt-16 text-center transition-all duration-400 ${isTransitioning ? 'opacity-0 transform translate-y-5' : ''}`}>
            <p className="text-xs text-text-muted mb-4 font-medium uppercase tracking-wider">Popular Questions</p>
            <div className="flex flex-wrap justify-center gap-3">
              <button
                onClick={() => handleSuggestionClick("What is my brand's health score?")}
                className="bg-card hover:bg-background-secondary border border-border text-text-secondary hover:text-foreground px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 flex items-center gap-2"
              >
                <i className="fas fa-heartbeat text-primary"></i>
                Brand Health Score
              </button>
              <button
                onClick={() => handleSuggestionClick("Compare traditional vs digital metrics")}
                className="bg-card hover:bg-background-secondary border border-border text-text-secondary hover:text-foreground px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 flex items-center gap-2"
              >
                <i className="fas fa-chart-bar text-cyan-500"></i>
                Traditional vs Digital
              </button>
              <button
                onClick={() => handleSuggestionClick("Show demographic insights")}
                className="bg-card hover:bg-background-secondary border border-border text-text-secondary hover:text-foreground px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 flex items-center gap-2"
              >
                <i className="fas fa-users text-secondary"></i>
                Demographics
              </button>
            </div>
          </div>

          {/* Disclaimer */}
          <div className="mt-8 text-center">
            <p className="text-xs text-text-muted">
              <span className="text-primary font-medium">BrandHealth 360</span> provides AI-powered analytics based on available data.
            </p>
          </div>
        </div>

        {/* Loading overlay during transition */}
        {isStreaming && (
          <div className="fixed inset-0 flex items-center justify-center z-50 bg-background/80 backdrop-blur-sm">
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-full flex items-center justify-center shadow-lg mb-4">
                <i className="fas fa-heartbeat text-white text-xl animate-pulse"></i>
              </div>
              <p className="text-text-secondary">Analyzing your query...</p>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Chat view - messages exist
  return (
    <div className="min-h-screen flex flex-col bg-background">
      {/* Chat Sidebar */}
      <ChatSidebar />

      {/* Header */}
      <div className="border-b border-border bg-card/80 backdrop-blur-md sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center">
            <div className="w-24 h-10 relative flex items-center justify-center mr-2">
              <img
                src="/sila-wordmark.png"
                alt="Sila Logo"
                className="w-full h-full object-contain"
              />
            </div>
            <span className="text-sm font-medium text-text-secondary">BrandHealth 360</span>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={clearChat}
              className="px-3 py-1.5 bg-primary/20 hover:bg-primary/30 text-primary rounded-lg transition-colors text-sm"
            >
              <i className="fas fa-plus mr-2"></i>
              New Chat
            </button>
            <SidebarToggleButton />
          </div>
        </div>
      </div>

      {/* Messages area */}
      <div className="flex-1 overflow-y-auto px-4 py-6">
        <div className="max-w-4xl mx-auto">
          {messages.map((message) => (
            <div key={message.id} className={`mb-6 flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%] ${message.role === 'user' ? 'order-2' : 'order-1'}`}>
                <div className={`flex items-start gap-3 ${message.role === 'user' ? 'flex-row-reverse' : ''}`}>
                  <div className={`min-w-10 min-h-10 w-10 h-10 rounded-full flex items-center justify-center shadow-lg ${
                    message.role === 'user'
                      ? 'bg-gradient-primary'
                      : 'bg-gradient-to-br from-cyan-500 to-blue-600'
                  }`}>
                    <i className={`fas ${message.role === 'user' ? 'fa-user' : 'fa-heartbeat'} text-white text-base`}></i>
                  </div>
                  <div className={`${
                    message.role === 'user'
                      ? 'text-white rounded-2xl p-4 px-5 bg-gradient-primary shadow-modern'
                      : 'bg-card border border-border rounded-2xl p-4 px-5 shadow-modern text-foreground'
                  }`}>
                    {message.role === 'user' ? (
                      <p className="text-base leading-relaxed whitespace-pre-wrap">{message.content}</p>
                    ) : (
                      <EnhancedMessage
                        content={message.content}
                        metrics={message.metrics}
                        competitors={message.competitors}
                        demographics={message.demographics}
                        followUpQuestions={message.followUpQuestions}
                        onFollowUpClick={handleFollowUpClick}
                        ragSources={message.ragSources}
                        showCharts={message.showCharts !== false}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}

          {/* Loading animation */}
          {isStreaming && messages.length > 0 && !streamingContent && (
            <div className="flex mb-6">
              <div className="flex items-start gap-3">
                <div className="min-w-10 min-h-10 w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-full flex items-center justify-center shadow-lg">
                  <i className="fas fa-heartbeat text-white text-base animate-pulse"></i>
                </div>
                <div className="bg-card border border-border rounded-2xl p-4 px-5">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input area - at bottom when chatting */}
      <div className="border-t border-border bg-card/80 backdrop-blur-md p-4">
        <div className="max-w-4xl mx-auto">
          <div className="bg-background-secondary border border-border rounded-2xl p-2 flex items-center">
            <AutoExpandingTextArea
              ref={inputRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage(inputValue);
                }
              }}
              placeholder="Type your message..."
              disabled={isStreaming}
              className="flex-1 bg-transparent text-foreground placeholder-text-muted px-4 py-3
                focus:outline-none text-base transition-all duration-200 resize-none border-0"
              rows={1}
            />

            <button
              onClick={() => handleSendMessage(inputValue)}
              disabled={!inputValue.trim() || isStreaming}
              className={`mr-2 p-3 rounded-xl transition-all duration-200
                ${!inputValue.trim() || isStreaming
                  ? 'text-text-muted cursor-not-allowed opacity-50'
                  : 'bg-primary hover:bg-primary/90 text-white shadow-md hover:shadow-lg transform hover:scale-105 active:scale-95'
                }`}
            >
              <i className={`fas ${isStreaming ? 'fa-spinner fa-spin' : 'fa-paper-plane'} text-lg`}></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LandingChatPage;
